import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSaveData } from '../../../hooks/useSaveData';
import { usersApiKeyStore } from '../../../api/users-api-key-store';
import { Alert, Button, Dialog, Grid, Snackbar } from '@mui/material';
import { FormAutoCompleteMultiSelectDynamic } from '../../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { marketAreaApiKeyStore } from '../../../api/masters/market-area-api-key-store';
import { skillSetApiKeyStore } from '../../../api/masters/skill-set-api-key-store';
import { equipmentApiKeyStore } from '../../../api/masters/equipment-api-key-store';
import { SiTrackGrid } from '../../../components/si-track-grid/si-track-grid';
import { inspectorsColumns } from './inspectors-grid-columns';
import { UserPermissionName } from '../../../models/user-permission-name';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';

export const SelectInspector = () => {
  const { setValue, getValues } = useFormContext();
  const [equipmentIDs, setEquipmentIDs] = useState<any>();
  const [skillsetIDs, setSkillsetIDs] = useState<any>();
  const [marketAreaIDs, setMarketAreaIDs] = useState<any>();
  const [userIDs, setUserIDs] = useState<any>();
  const [completeData, setCompleteData] = useState<any[]>();
  const [isOpenSnackbar, setIsOpenSnackbar] = useState(false);
  const [message, setMessage] = useState<string>();
  const { isLoading, data, mutateAsync } = useSaveData<any>(usersApiKeyStore.updateInspectors());

  const setInspectorData = (inspectorData: any) => {
    const selectedID = getValues('inspectorID');
    const updatedData = inspectorData?.map((d: any) => ({
      ...d,
      selected: selectedID === d.userID,
    }));
    setCompleteData(updatedData);
  }

  useEffect(() => {
    if (data?.data) {
      setInspectorData(data?.data);
    }
  }, [data]);

  const handleSelectedInspector = (values: any[]) => {
    if (values?.length == 0) { setValue('inspectorID', 0); }
    else if (values && values.length > 0) {
      if (values[0]?.hasDuplicateEquipments) {
        setIsOpenSnackbar(true);
        setMessage(values[0]?.equipmentDuplicateWarningMsg)
        setInspectorData(data?.data);
        setValue('inspectorID', 0);
      }
      else {
        setValue('inspectorID', values[0].userID);
      }
    }
  };

  useEffect(() => {
    mutateAsync({
      data: {
        workOrderID: getValues('workOrderID'),
      },
    });
  }, []);

  const handleFilterClick = () => {
    mutateAsync({
      data: {
        skillsets: skillsetIDs?.map((p: any) => p.skillsetID),
        equipments: equipmentIDs?.map((p: any) => p.equipmentID),
        marketAreas: marketAreaIDs?.map((p: any) => p.marketAreaID),
        inspectors: userIDs?.map((p: any) => p.userID),
        workOrderID: getValues('workOrderID'),
      },
    });
  };

  return (
    <>
      <Grid container spacing={1}>
        <Grid item lg={3} sm={6} xl={3} xs={6}>
          <FormAutoCompleteMultiSelectDynamic
            name="userIDs"
            label="Inspectors"
            getQueryKeyConfig={() => usersApiKeyStore.getUsersByRole('ins')}
            optionsLabelKey="fullName"
            optionsValueKey="userID"
            onChange={(values: any) => setUserIDs(values)}
          />
        </Grid>
        <Grid item lg={3} sm={6} xl={3} xs={6}>
          <FormAutoCompleteMultiSelectDynamic
            name="marketAreaIDs"
            label="Market Areas"
            getQueryKeyConfig={() => marketAreaApiKeyStore.getMarketAreas()}
            optionsLabelKey="marketAreaName"
            optionsValueKey="marketAreaID"
            onChange={(values: any) => setMarketAreaIDs(values)}
          />
        </Grid>
        <Grid item lg={3} sm={6} xl={3} xs={6}>
          <FormAutoCompleteMultiSelectDynamic
            name="skillsetIDs"
            label="Skills"
            getQueryKeyConfig={() => skillSetApiKeyStore.getSkillSets()}
            optionsLabelKey="skillsetName"
            optionsValueKey="skillsetID"
            onChange={(values: any) => setSkillsetIDs(values)}
          />
        </Grid>
        <Grid item xl={2} lg={3} sm={6} xs={6}>
          <FormAutoCompleteMultiSelectDynamic
            name="equipmentIDs"
            label="Equipments"
            getQueryKeyConfig={() => equipmentApiKeyStore.getEquipments()}
            optionsLabelKey="equipmentName"
            optionsValueKey="equipmentID"
            onChange={(values: any) => setEquipmentIDs(values)}
          />
        </Grid>

        <Grid item xl={1} lg={1} sm={3} xs={6}>
          <Button type="button" onClick={handleFilterClick} title="Filter" className="btn btn-md btn-primary">
            Search
          </Button>
        </Grid>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <SiTrackGrid
            fileTitle='workorder_inspector'
            columns={inspectorsColumns}
            dataItemKey="userID"
            selectedField="selected"
            selectionMode="single"
            onSelectedItems={handleSelectedInspector}
            dataItems={completeData}
            actionPermissions={{
              export: UserPermissionName.DOWNLOAD_GRID,
            }}
            isBusy={isLoading}
          />
        </Grid>
      </Grid>
      <Snackbar
        open={isOpenSnackbar}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setIsOpenSnackbar(false)}
      >
        <Alert severity="error" variant="filled" sx={{ width: '100%' }} onClose={() => setIsOpenSnackbar(false)}>
          {message}
        </Alert>
      </Snackbar>
    </>
  );
};
